import { useMemo, useEffect, useState } from "react";
import Modal from '../Modal';
import styles from './authModal.module.scss';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ButtonShared from '../Button';
import TextFieldShared from '../TextField/TextField';
import { images } from "../../constants/images";
import { useGoogleLogin, googleLogout, useGoogleOneTapLogin } from '@react-oauth/google';
import { useIsMobileDevice, useToast } from "../../helpers/hooks";
import Recovery from '../../pages/Login/Recovery';
import Code from '../../pages/Login/Code';

interface AuthModalProps {
    open: boolean;
    onClose: () => void;
    onSwitchToRegister: (isGoogle?: boolean) => void;
    fetchingUser: string;
    errorUser: string;
    loginRequest: Function;
    externalLoginRequest: Function;
    externalLogin: any;
    resetPassword: Function;
    resetPasswordReducer: any;
    updatePassword: Function;
    updatePasswordReducer: any;
    cleanErrorRegister: Function;
    profile: string;
    logoutUser: Function;
    getNotifications: Function;
}

const AuthModal = ({
    open,
    onClose,
    onSwitchToRegister,
    fetchingUser,
    errorUser,
    loginRequest,
    externalLoginRequest,
    externalLogin,
    resetPassword,
    resetPasswordReducer,
    updatePassword,
    updatePasswordReducer,
    cleanErrorRegister,
    profile,
    logoutUser,
    getNotifications
}: AuthModalProps) => {
    const toast = useToast();
    const isMobileDevice = useIsMobileDevice();
    const [cardContent, setCardContent] = useState<string>("login");
    const [disabled, setDisabled] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [showSuccess, setShowSuccess] = useState<boolean>(false);
    const [email, setEmail] = useState<any>({ value: "", error: "" });
    const [password, setPassword] = useState<any>({ value: "", error: "" });
    const [emailRecovery, setEmailRecovery] = useState<any>({ value: "", error: "" });
    const [code, setCode] = useState<any>({ value: "", error: "" });
    const [passwordCode, setPasswordCode] = useState<any>({
        value: "",
        error: "",
        validation: {
            specialCaracter: false,
            mayus: false,
            correctLength: false,
            number: false
        }
    });

    const handleClickResendCode = () => {
        resetPassword(resetPasswordReducer.email);
    }

    const handleSwitchForm = () => {
        onSwitchToRegister(false);
        handleClose();
    }

    const handleChangeCard = (content: string) => {
        setEmailRecovery({ ...emailRecovery, error: "" });
        setCode({ value: "", error: "" });
        setPasswordCode({ value: "", error: "" });
        setPasswordCode({
            ...passwordCode, validation: {
                specialCaracter: false,
                mayus: false,
                correctLength: false,
                number: false
            }
        });
        setDisabled(true);
        setCardContent(content)
        cleanErrorRegister();
    };

    useGoogleOneTapLogin({
        onSuccess: credentialResponse => externalLoginRequest("google", credentialResponse),
        onError: () => toast("Ha ocurrido un error al loguearte"),
        cancel_on_tap_outside: false,
        disabled: isMobileDevice || !open
    });

    const googleLogin = useGoogleLogin({
        onSuccess: (response: any) => externalLoginRequest("google", response),
        onError: () => toast("Ha ocurrido un error al loguearte")
    });

    const handlePress = () => {
        switch (cardContent) {
            case "login":
                return handleSave();
            case "recovery":
                return handleRecovery();
            default:
                return handleCode();
        }
    }

    const handleSave = () => {
        if (!email?.value) return setEmail({ ...email, error: "Debes ingresar un email" });
        if (!password?.value) return setPassword({ ...password, error: "Debes ingresar una contraseña" });

        loginRequest({
            email: email?.value.toLowerCase(),
            password: password?.value
        });
    }

    const handleRecovery = () => {
        if (!emailRecovery?.value) return setEmailRecovery({ ...emailRecovery, error: "Debes ingresar un email" });
        resetPassword(emailRecovery?.value);
    }

    const handleCode = () => {
        if (!code?.value) return setCode({ ...code, error: "Codigo inválido" });
        if (!passwordCode?.value) return setPasswordCode({ ...passwordCode, error: "" });
        updatePassword(resetPasswordReducer.email, code?.value.toString(), passwordCode?.value);
    }

    const handleClose = (): void => {
        onClose();
        setCardContent("login");
        setEmail({ value: "", error: "" });
        setPassword({ value: "", error: "" });
        setEmailRecovery({ value: "", error: "" });
        setCode({ value: "", error: "" });
        setPasswordCode({
            value: "", error: "", validation: {
                specialCaracter: false,
                mayus: false,
                correctLength: false,
                number: false
            }
        });
    };

    useEffect(() => {
        if (externalLogin) {
            toast("Termina de completar los datos de tu cuenta");
            handleClose();
            onSwitchToRegister(true);
        }
    }, [externalLogin]);

    useEffect(() => {
        setDisabled((email?.value !== "" && password?.value !== "") ? false : true);
    }, [email?.value, password?.value]);

    useEffect(() => {
        setDisabled((emailRecovery?.value !== "") ? false : true);
    }, [emailRecovery?.value]);

    useEffect(() => {
        setDisabled((code?.value !== "" && passwordCode?.value !== "") ? false : true);
    }, [code?.value, passwordCode?.value]);

    useEffect(() => {
        if (resetPasswordReducer.fetching === "loading") {
            setLoading(true);
        }
        if (resetPasswordReducer.fetching === "failure") {
            if (resetPasswordReducer?.error === "EMAIL_INVALID_EMAIL") {
                setEmailRecovery({ ...emailRecovery, error: "Ingresa un email válido" });
            }
            if (resetPasswordReducer?.error === "USER_INVALID") {
                setEmailRecovery({ ...emailRecovery, error: "El email no pertenece a ningún usuario" });
            }
            if (resetPasswordReducer?.error === "USER_DISABLED") {
                setEmailRecovery({ ...emailRecovery, error: "El usuario se encuentra deshabilitado" });
            }
            setLoading(false);
            cleanErrorRegister();
        }
        if (resetPasswordReducer.fetching === "success") {
            setLoading(false);
            toast("Se ha enviado el código de recuperación a tu email");
            handleChangeCard('code');
        }
    }, [resetPasswordReducer.fetching]);

    useEffect(() => {
        if (updatePasswordReducer.fetching === "loading") {
            setLoading(true);
        }
        if (updatePasswordReducer.fetching === "failure") {
            if (updatePasswordReducer?.error === "INVALID_CODE") {
                setCode({ ...code, error: "Codigo inválido" });
            }
            setLoading(false);
            cleanErrorRegister();
        }
        if (updatePasswordReducer.fetching === "success") {
            toast("Modificaste tu clave correctamente, inicia sesión");
            setLoading(false);
            handleChangeCard('login');
        }
    }, [updatePasswordReducer.fetching]);

    useEffect(() => {
        if (fetchingUser === "loading") {
            setLoading(true);
        }
        if (fetchingUser === "failure") {
            setLoading(false);
            cleanErrorRegister();
            setEmail({ ...email, error: "" });
            setPassword({ ...password, error: "" });
            if (errorUser.toLowerCase() === "too many requests") {
                return toast("Espera 1 minuto");
            }
            if (errorUser === "INVALID_USER") {
                return setEmail({ ...email, error: "Usuario incorrecto" });
            }
            if (errorUser === "NOT_FOUND_USER") {
                return toast("Usuario no encontrado");
            }
            if (errorUser === "INVALID_PASSWORD") {
                return setPassword({ ...password, error: "Contraseña invalida" });
            }
            if (errorUser === "EMAIL_INVALID_EMAIL" || errorUser === '"email" must be a valid email') {
                return setEmail({ ...email, error: "Ingresa un email válido" });
            }
            if (errorUser === "USER_DISABLED") {
                return toast("Usuario deshabilitado");
            }
            return toast("Ha ocurrido un error");
        }
        if (fetchingUser === "success" && profile !== "final-user" && !externalLogin) {
            setLoading(false);
            cleanErrorRegister();
            setEmail({ ...email, error: "Usuario incorrecto" });
            setPassword({ ...password, error: "" });
            googleLogout();
            logoutUser();
            return;
        }
        if (fetchingUser === "success" && !externalLogin) {
            setLoading(false);
            cleanErrorRegister();
            setShowSuccess(true);
            getNotifications();
            setTimeout(() => {
                setShowSuccess(false);
                handleClose();
            }, 1500);
        }
    }, [fetchingUser, profile]);

    const isButtonDisabled: any = useMemo(() => {
        return disabled || (cardContent === "code" && !(passwordCode?.validation?.specialCaracter && passwordCode?.validation?.mayus && passwordCode?.validation?.correctLength && passwordCode?.validation?.number))
    }, [disabled, passwordCode]);

    useEffect(() => {
        if (open) {
            cleanErrorRegister();
        }
    }, [open]);

    return (
        <Modal variant="" open={open} onClose={handleClose} className={styles.modal}>
            <div className={styles.content}>
                {showSuccess ? (
                    <div className={styles.successContainer}>
                        <div className={styles.checkCircle}>
                            <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                                <circle cx="40" cy="40" r="38" stroke="#4CAF50" strokeWidth="4" fill="none" />
                                <path d="M25 40L35 50L55 30" stroke="#4CAF50" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <h2 className={styles.successTitle}>¡Todo listo!</h2>
                        <p className={styles.successMessage}>Ya puedes seguir explorando Linze</p>
                    </div>
                ) : (
                    <>
                        <div className={styles.btnClose}>
                            <IconButton onClick={handleClose} size="small">
                                <CloseIcon />
                            </IconButton>
                        </div>
                        
                        <div className={styles.iconContainer}>
                            <img src={images.logoNLinze} className={styles.icon}/>
                        </div>

                        <h2 className={styles.title}>
                            {cardContent === "recovery" ? "Recuperar contraseña" : cardContent === "code" ? "Código de recuperación" : "Ingresa a tu cuenta"}
                        </h2>

                        <div className={styles.form}>
                            {cardContent === "recovery" && (
                                <Recovery
                                    emailRecovery={emailRecovery}
                                    setEmailRecovery={setEmailRecovery}
                                    handlePress={handlePress}
                                    handleChange={handleChangeCard}
                                />
                            )}

                            {cardContent === "login" && (
                                <div className={styles.loginContent}>
                                    <div className={styles.inputs}>
                                        <TextFieldShared
                                            label="Email"
                                            name="email"
                                            value={email?.value}
                                            onChange={(value: any) => setEmail({ ...email, value: value })}
                                            onEnter={handleSave}
                                            error={email?.error}
                                            autoFocus
                                            variant="loginForm"
                                        />
                                        <TextFieldShared
                                            label="Contraseña"
                                            name="password"
                                            value={password?.value}
                                            onChange={(value: any) => setPassword({ ...password, value: value })}
                                            onEnter={handleSave}
                                            error={password?.error}
                                            hidden
                                            variant="loginForm"
                                        />
                                    </div>
                                    <ButtonShared
                                        title="Olvidé mi contraseña"
                                        fontSize="small"
                                        spanClassName={styles.forgetPassword}
                                        onPress={() => handleChangeCard('recovery')}
                                    />
                                </div>
                            )}

                            {cardContent === "code" && (
                                <Code
                                    handlePress={handlePress}
                                    code={code}
                                    setCode={setCode}
                                    password={passwordCode}
                                    setPassword={setPasswordCode}
                                />
                            )}
                        </div>
                        <ButtonShared
                            title={(cardContent === "login") ? "Ingresar" : "Enviar"}
                            className={styles.submit}
                            disabled={isButtonDisabled}
                            primaryLogin
                            onPress={handlePress}
                            loading={loading}
                        />
                        {cardContent === "code" && (
                            <p className={styles.resendCode}>
                                <span>¿No recibiste el código?</span>
                                <span className={styles.button} onClick={handleClickResendCode}>Reenviar</span>
                            </p>
                        )}
                        {cardContent === "login" && (
                            <>
                                <div className={styles.google}>
                                    <div className={styles.divisor}>
                                        <hr />
                                        <p>O ingresa con</p>
                                        <hr />
                                    </div>
                                    <IconButton className={styles.button} aria-label='Google' onClick={() => googleLogin()}>
                                        <img src={images.google} alt="google" title="google" style={{ width: "24px", height: "24px" }} />
                                    </IconButton>
                                </div>
                                <div className={styles.link}>
                                    <p>¿No tienes cuenta?</p>
                                    <span className={styles.redirect} onClick={handleSwitchForm}>Regístrate</span>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
};

export default AuthModal;
